import { useEffect, useMemo, useState } from 'react';
import { Box, Portal } from '@mui/material';
import { EmailGenerationProgressCard } from 'app/NewAIAvatarLayout/features/MiaChat/components/EmailGenerationProgressCard';
import type { EmailGenerationProgressCardProps } from 'app/NewAIAvatarLayout/features/MiaChat/types';
import SegmentAttentionProgressDialog from 'app/pages/HumanInTheLoopPage/components/SegmentAttentionProgressDialog';

type HilSegmentAttentionProgressLayerProps = {
  automationInProgress: boolean;
  showResolveProgress: boolean;
  retryAllInProgress: boolean;
  automationStreamingDataForCard: EmailGenerationProgressCardProps['streamingData'];
  automationCurrentSectionForCard: EmailGenerationProgressCardProps['currentSection'];
  automationProgressPercentage: number;
  automationProgressCurrent: number;
  automationProgressTotal: number;
  automationLatestStatusText: string;
  automationLatestStepLabel: string;
  automationLastUpdatedAt: number | null;
};

export default function HilSegmentAttentionProgressLayer({
  automationInProgress,
  showResolveProgress,
  retryAllInProgress,
  automationStreamingDataForCard,
  automationCurrentSectionForCard,
  automationProgressPercentage,
  automationProgressCurrent,
  automationProgressTotal,
  automationLatestStatusText,
  automationLatestStepLabel,
  automationLastUpdatedAt,
}: HilSegmentAttentionProgressLayerProps) {
  const [segmentProgressDetailsOpen, setSegmentProgressDetailsOpen] = useState(false);

  useEffect(() => {
    if (showResolveProgress && automationInProgress) {
      setSegmentProgressDetailsOpen(true);
    }
  }, [showResolveProgress, automationInProgress]);

  const progressTitle = useMemo(
    () =>
      retryAllInProgress
        ? 'Retrying all segments...'
        : 'Resolving segment issues...',
    [retryAllInProgress],
  );

  const dialogTitle = useMemo(
    () =>
      retryAllInProgress
        ? 'Retry all segments progress'
        : 'Segment resolve progress',
    [retryAllInProgress],
  );

  if (!automationInProgress || !showResolveProgress) {
    return (
      <SegmentAttentionProgressDialog
        open={segmentProgressDetailsOpen}
        onClose={() => setSegmentProgressDetailsOpen(false)}
        title={dialogTitle}
        progressCurrent={automationProgressCurrent}
        progressTotal={automationProgressTotal}
        progressPercentage={automationProgressPercentage}
        latestStatusText={automationLatestStatusText}
        latestStepLabel={automationLatestStepLabel}
        lastUpdatedAt={automationLastUpdatedAt}
      />
    );
  }

  return (
    <>
      <Portal>
        <Box
          sx={{
            position: 'fixed',
            bottom: 24,
            right: 24,
            zIndex: 1300,
            minWidth: 520,
          }}
        >
          <EmailGenerationProgressCard
            isGeneratingEmail={automationInProgress}
            streamingData={automationStreamingDataForCard}
            currentSection={automationCurrentSectionForCard}
            progressOverride={automationProgressPercentage}
            title={progressTitle}
            onOpenEmailGenerationPopup={() => setSegmentProgressDetailsOpen(true)}
          />
        </Box>
      </Portal>

      <SegmentAttentionProgressDialog
        open={segmentProgressDetailsOpen}
        onClose={() => setSegmentProgressDetailsOpen(false)}
        title={dialogTitle}
        progressCurrent={automationProgressCurrent}
        progressTotal={automationProgressTotal}
        progressPercentage={automationProgressPercentage}
        latestStatusText={automationLatestStatusText}
        latestStepLabel={automationLatestStepLabel}
        lastUpdatedAt={automationLastUpdatedAt}
      />
    </>
  );
}
